import React, { useState } from "react";
import { PageHeader } from "../components/layout/Shell";
import StatutoryShell from "../components/statutory/StatutoryShell";
import { StatutoryProvider } from "../components/statutory/StatutoryContext";
import { StatutoryBadge, StatutoryStatePanel } from "../components/statutory/StatutoryBadge";
import { openQuestions, transitionGates } from "../mock/statutory/mockData";
import { ChevronDown, ChevronUp } from "lucide-react";

const PRIORITY_COLOR = {
  P0: "bg-red-100 text-red-700 border-red-300",
  P1: "bg-orange-100 text-orange-700 border-orange-300",
  P2: "bg-blue-100 text-blue-700 border-blue-300",
};

const WAVE_COLOR = {
  "Wave 1": "bg-green-100 text-green-700",
  "Wave 2": "bg-amber-100 text-amber-700",
  "Wave 3": "bg-orange-100 text-orange-700",
};

const BLOCKER_FILTERS = ["ALL", "ARB", "Statutory Analyst", "Counsel", "Open", "Blocked", "Ready for decision"];

function OQRow({ oq }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr
        className="hover:bg-muted/30 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid={`oq-row-${oq.id}`}
      >
        <td className="px-3 py-3 font-mono font-bold text-foreground">{oq.id}</td>
        <td className="px-3 py-3">
          <span className={`text-xs px-1.5 py-0.5 rounded border font-bold ${PRIORITY_COLOR[oq.priority]}`}>{oq.priority}</span>
        </td>
        <td className="px-3 py-3 text-xs text-muted-foreground">{oq.category}</td>
        <td className="px-3 py-3 text-xs font-mono text-muted-foreground">{oq.jurisdiction}</td>
        <td className="px-3 py-3">
          <div className="text-sm font-medium text-foreground">{oq.title}</div>
        </td>
        <td className="px-3 py-3 text-xs">{oq.owner}</td>
        <td className="px-3 py-3"><StatutoryBadge state={oq.status === "OPEN" ? "OPEN" : oq.status} /></td>
        <td className="px-3 py-3">
          {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-muted/20" data-testid={`oq-detail-${oq.id}`}>
          <td colSpan={8} className="px-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Description</div>
                <p className="text-sm text-foreground">{oq.description}</p>
                {oq.checklistProgress && (
                  <p className="mt-2 text-xs text-amber-700 font-medium">Checklist: {oq.checklistProgress}</p>
                )}
              </div>
              <div className="space-y-2">
                <div>
                  <div className="text-xs text-muted-foreground">Evidence Required</div>
                  <div className="text-sm text-foreground">{oq.evidenceRequired}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Next Action</div>
                  <div className="text-sm font-medium text-primary">{oq.nextAction}</div>
                </div>
                {oq.blockedEntities && (
                  <div>
                    <div className="text-xs text-muted-foreground">Currently Blocking</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {oq.blockedEntities.map((e) => (
                        <span key={e} className="text-xs font-mono bg-red-50 text-red-700 border border-red-200 rounded px-1.5 py-0.5">{e}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function TGRow({ gate }) {
  const isBlocked = gate.gateStatus === "EXTERNALLY_BLOCKED";
  return (
    <tr
      className={`${isBlocked ? "bg-red-50/30" : ""}`}
      data-testid={`tg-row-${gate.id}`}
    >
      <td className="px-3 py-3 font-mono font-bold text-foreground">{gate.id}</td>
      <td className="px-3 py-3">
        <div className="text-sm font-medium">{gate.title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{gate.criterion}</div>
      </td>
      <td className="px-3 py-3 text-xs text-muted-foreground">{gate.artifactStatus}</td>
      <td className="px-3 py-3">
        <StatutoryBadge state={gate.gateStatus === "EXTERNALLY_BLOCKED" ? "EXTERNALLY_BLOCKED" : "OPEN"} />
      </td>
      <td className="px-3 py-3 text-xs text-muted-foreground">{gate.gateStatusDetail}</td>
      <td className="px-3 py-3">
        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${WAVE_COLOR[gate.wave] || "bg-gray-100 text-gray-600"}`}>{gate.wave}</span>
      </td>
    </tr>
  );
}

function OQContent() {
  const [filter, setFilter] = useState("ALL");

  const filteredOQs = openQuestions.filter((oq) => {
    if (filter === "ALL") return true;
    if (filter === "ARB") return oq.category === "ARB";
    if (filter === "Statutory Analyst") return oq.category === "Statutory Analyst";
    if (filter === "Counsel") return oq.category === "Counsel";
    if (filter === "Open") return oq.status === "OPEN";
    if (filter === "Blocked") return oq.status !== "OPEN";
    return true;
  });

  return (
    <div className="space-y-8">
      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {BLOCKER_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs px-2.5 py-1 rounded border transition-colors ${filter === f ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:text-foreground"}`}
            data-testid={`oq-filter-${f.replace(/ /g, "-").toLowerCase()}`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* OQ Register */}
      <div>
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
          Open Questions Register (OQ-V4-1 through OQ-V4-7)
        </div>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-xs" data-testid="oq-table">
            <thead className="bg-muted/40">
              <tr>
                {["ID", "Priority", "Category", "Jurisdiction", "Title", "Owner", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredOQs.map((oq) => <OQRow key={oq.id} oq={oq} />)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transition Gates */}
      <div>
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
          Transition Gates (TG-V4-1 through TG-V4-6)
        </div>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-xs" data-testid="tg-table">
            <thead className="bg-muted/40">
              <tr>
                {["Gate", "Criterion", "Artifacts", "Gate Status", "Detail", "Wave"].map((h) => (
                  <th key={h} className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {transitionGates.map((gate) => <TGRow key={gate.id} gate={gate} />)}
            </tbody>
          </table>
        </div>
        <div className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
          <p className="text-xs text-amber-800">
            <span className="font-bold">Gate PASS = 0 / 6.</span>{" "}
            Gates PASS only when ARB issues an explicit decision. Artifact completeness ≠ gate passage.
            W11-L-IMPL remains BLOCKED / NOT AUTHORIZED.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function StatutoryOpenQuestions() {
  return (
    <StatutoryProvider>
      <PageHeader eyebrow="Statutory Configuration" title="Questions & Blockers" description="OQ register (OQ-V4-1..V4-7) and transition gates (TG-V4-1..TG-V4-6)." />
      <StatutoryShell>
        <OQContent />
      </StatutoryShell>
    </StatutoryProvider>
  );
}
